import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  ClipboardCheck,
  Eye,
  Lock,
  Upload,
  Plus,
  CheckCircle2,
  Loader2,
  Boxes,
  ScanLine,
  Trash2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Pagination } from '../components/ui/pagination';
import { LoadingTable } from '../components/ui/loading-states';
import { ErrorState } from '../components/ui/error-state';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import {
  useMarkirovkaReconSessions,
  useMarkirovkaReconSession,
  useMarkirovkaReconDiscrepancies,
  useOpenReconSession,
  useUpdateReconSession,
  useCloseReconSession,
  useExportReconSession,
  useSyncReconStock,
  usePreviewReconSession,
  useMarkirovkaReconCis,
  useDeleteReconSession,
} from '../hooks/useMarkirovkaRecon';
import type {
  MarkirovkaReconSession,
  MarkirovkaReconSessionFilters,
  MarkirovkaReconPreview,
  MarkirovkaReconCisRow,
  MarkirovkaCisKind,
} from '../types/markirovka-stock.types';

const PAGE_SIZE = 50;

const REASON_LABELS: Record<string, string> = {
  off_stock: 'Нет на остатках edo',
  excluded_doc: 'Накладная вне учёта',
  excluded_document: 'Накладная вне учёта (госпоставка)',
  not_in_stock: 'Нет в учёте',
  foreign_owner: 'Чужой владелец',
  unknown: 'Не определено',
};

const SNAPSHOT_STATUS_LABELS: Record<string, string> = {
  none: 'не зафиксирован',
  running: 'создаётся…',
  ready: 'зафиксирован',
  failed: 'ошибка',
};

/** Почему документ нельзя закрыть (коды close_blockers с бэкенда). */
const CLOSE_BLOCKER_LABELS: Record<string, string> = {
  SNAPSHOT_REQUIRED:
    'Закрытие недоступно: сначала зафиксируйте учётный остаток (шаг 1).',
  UNLINKED_MARKS_REMAIN:
    'Закрытие заблокировано: в учёте есть марки без накладной-источника (см. предупреждение выше).',
};

function formatDateTime(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleString('ru-RU') : '—';
}

function isoToDate(iso?: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}
function dateToIso(date: string, endOfDay = false): string | undefined {
  if (!date) return undefined;
  return `${date}T${endOfDay ? '23:59:59' : '00:00:00'}Z`;
}
function formatDate(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('ru-RU') : '—';
}

function OnecBadge({ status }: { status: string }) {
  if (status === 'synced')
    return (
      <Badge variant="default" className="gap-1">
        <CheckCircle2 className="w-3 h-3" />
        Отправлено
      </Badge>
    );
  if (status === 'pending') return <Badge variant="outline">Ожидает</Badge>;
  if (status === 'failed')
    return <Badge variant="destructive">Ошибка</Badge>;
  return <span className="text-muted-foreground">—</span>;
}

export function MarkirovkaRecon() {
  const [filters, setFilters] = useState<MarkirovkaReconSessionFilters>({
    page: 1,
    limit: PAGE_SIZE,
  });
  const [openDocId, setOpenDocId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<MarkirovkaReconSession | null>(null);

  const { data, isLoading, error, refetch } =
    useMarkirovkaReconSessions(filters);
  const openMutation = useOpenReconSession();
  const deleteMutation = useDeleteReconSession();

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(
    1,
    Math.ceil(total / (filters.limit ?? PAGE_SIZE)),
  );

  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      setActionError((e as Error)?.message || 'Ошибка операции');
    }
  };

  const createDocument = () =>
    void run(async () => {
      // Период не задаём — оператор выберет его в документе перед «Фактом».
      const created = await openMutation.mutateAsync({});
      setOpenDocId(created.id);
    });

  const setStatusFilter = (value: 'all' | 'open' | 'closed') =>
    setFilters((f) => ({
      ...f,
      status: value === 'all' ? undefined : value,
      page: 1,
    }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Сверка маркировки
          </h1>
          <p className="text-muted-foreground">
            Документ инвентаризации: за выбранный период берётся факт
            (отсканированные медикаменты), при закрытии подтягивается учётный
            остаток из edo и считаются расхождения. Затем — отправка в 1С.
          </p>
        </div>
        <Button onClick={createDocument} disabled={openMutation.isPending}>
          <Plus className="w-4 h-4 mr-1" />
          Новый документ
        </Button>
      </div>

      {actionError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {actionError}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Документы сверки</CardTitle>
          <CardDescription>
            Откройте документ, задайте период, закройте для расчёта расхождений и
            отправьте в 1С.
          </CardDescription>
          <div className="flex items-center gap-1 mt-2">
            {(['all', 'open', 'closed'] as const).map((v) => (
              <Button
                key={v}
                variant={
                  (filters.status === undefined && v === 'all') ||
                  filters.status === v
                    ? 'default'
                    : 'outline'
                }
                size="sm"
                onClick={() => setStatusFilter(v)}
              >
                {v === 'all' ? 'Все' : v === 'open' ? 'Открытые' : 'Закрытые'}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={8} columns={7} />
          ) : error ? (
            <ErrorState
              title="Не удалось загрузить документы"
              description={(error as Error)?.message}
              onAction={() => refetch()}
            />
          ) : rows.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              Документов пока нет — создайте первый
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Документ</TableHead>
                    <TableHead>Период</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead className="text-right">Недостача</TableHead>
                    <TableHead className="text-right">Излишек</TableHead>
                    <TableHead>1С</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((s) => (
                    <TableRow
                      key={s.id}
                      className="cursor-pointer"
                      onClick={() => setOpenDocId(s.id)}
                    >
                      <TableCell className="font-medium">
                        {s.title || 'Инвентаризация'}
                        <div className="text-xs text-muted-foreground">
                          {formatDate(s.started_at)}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDate(s.period_from)} — {formatDate(s.period_to)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            s.status === 'open' ? 'default' : 'secondary'
                          }
                        >
                          {s.status === 'open' ? 'Открыт' : 'Закрыт'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {s.status === 'closed' ? (
                          <span
                            className={
                              s.missing_total > 0
                                ? 'text-destructive font-semibold'
                                : ''
                            }
                          >
                            {s.missing_total}
                          </span>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {s.status === 'closed' ? (
                          <span
                            className={
                              s.surplus_total > 0
                                ? 'text-amber-600 font-semibold'
                                : ''
                            }
                          >
                            {s.surplus_total}
                          </span>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        {s.status === 'closed' ? (
                          <OnecBadge status={s.onec_sync_status} />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(s);
                            }}
                            aria-label="Удалить документ"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-4">
                <Pagination
                  currentPage={filters.page ?? 1}
                  totalPages={totalPages}
                  onPageChange={(p) => setFilters((f) => ({ ...f, page: p }))}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <DocumentDialog
        sessionId={openDocId}
        onClose={() => setOpenDocId(null)}
        onError={setActionError}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить документ?</AlertDialogTitle>
            <AlertDialogDescription>
              «{deleteTarget?.title || 'Инвентаризация'}» от{' '}
              {formatDate(deleteTarget?.started_at)} будет удалён вместе с
              расхождениями. Действие необратимо.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Отмена
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                const id = deleteTarget?.id;
                if (!id) return;
                void run(async () => {
                  await deleteMutation.mutateAsync(id);
                  if (openDocId === id) setOpenDocId(null);
                  setDeleteTarget(null);
                });
              }}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-1" />
              )}
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface DocumentDialogProps {
  sessionId: string | null;
  onClose: () => void;
  onError: (msg: string | null) => void;
}

function DocumentDialog({ sessionId, onClose, onError }: DocumentDialogProps) {
  const { data: session, isLoading } = useMarkirovkaReconSession(
    sessionId,
    !!sessionId,
  );

  const isOpen = session?.status === 'open';

  const run = async (fn: () => Promise<unknown>) => {
    onError(null);
    try {
      await fn();
    } catch (e) {
      onError((e as Error)?.message || 'Ошибка операции');
    }
  };

  return (
    <Dialog open={!!sessionId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" />
            {session?.title || 'Документ инвентаризации'}
            {session && (
              <Badge
                variant={isOpen ? 'default' : 'secondary'}
                className="ml-1"
              >
                {isOpen ? 'Открыт' : 'Закрыт'}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {session?.fact_source === 'session'
              ? 'Учёт — снимок остатков edo, зафиксированный для этой инвентаризации; факт — упаковки, отсканированные в рамках этой инвентаризации.'
              : 'Учёт — остатки из edo.markirovka; факт — отсканированные медикаменты за период (страница «Медикаменты»).'}
          </DialogDescription>
        </DialogHeader>

        {isLoading || !session ? (
          <LoadingTable rows={4} columns={3} />
        ) : isOpen ? (
          <OpenDocumentBody key={session.id} session={session} run={run} />
        ) : (
          <ClosedDocumentBody session={session} run={run} />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Открытый документ: учёт → период → факт → закрытие. */
function OpenDocumentBody({
  session,
  run,
}: {
  session: MarkirovkaReconSession;
  run: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const updateMutation = useUpdateReconSession();
  const closeMutation = useCloseReconSession();
  const syncStockMutation = useSyncReconStock();
  const previewMutation = usePreviewReconSession();

  // Документы с 0100: учёт — снимок, фиксируется один раз; факт — сканы документа.
  const isSnapshotModel = session.fact_source === 'session';
  const snapshotReady = session.snapshot_status === 'ready';
  const snapshotLocked =
    isSnapshotModel && snapshotReady && (session.scanned_in_period ?? 0) > 0;
  const closeBlockers = session.close_blockers ?? [];

  const [preview, setPreview] = useState<MarkirovkaReconPreview | null>(null);
  // 'empty' → нет данных; 'stock' → загружен учёт; 'fact' → сопоставлен факт.
  const [stage, setStage] = useState<'empty' | 'stock' | 'fact'>(
    isSnapshotModel && snapshotReady ? 'stock' : 'empty',
  );
  const [cisKind, setCisKind] = useState<MarkirovkaCisKind>('missing');
  const [cisPage, setCisPage] = useState(1);

  const fillStock = () =>
    void run(async () => {
      const res = await syncStockMutation.mutateAsync(session.id);
      setPreview(res);
      setStage('stock');
    });

  const fillFact = () =>
    void run(async () => {
      const res = await previewMutation.mutateAsync(session.id);
      setPreview(res);
      setCisKind('missing');
      setCisPage(1);
      setStage('fact');
    });

  const showFact = stage === 'fact';

  return (
    <div className="space-y-4">
      {/* Шаг 1 — учётный остаток */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-muted/30 p-3">
        <div className="text-sm">
          <div className="font-medium">1. Учётный остаток</div>
          <div className="text-muted-foreground">
            {isSnapshotModel
              ? snapshotLocked
                ? 'Снимок зафиксирован, инвентаризация идёт — заменить его нельзя'
                : 'Зафиксировать снимок остатков edo — один раз на инвентаризацию'
              : 'Подтянуть остатки из edo.markirovka'}
          </div>
        </div>
        <Button
          variant={stage === 'empty' ? 'default' : 'outline'}
          disabled={
            syncStockMutation.isPending ||
            snapshotLocked ||
            session.snapshot_status === 'running'
          }
          onClick={fillStock}
        >
          {syncStockMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <Boxes className="w-4 h-4 mr-1" />
          )}
          {isSnapshotModel && snapshotReady
            ? 'Снимок зафиксирован'
            : 'Заполнить учётный остаток'}
        </Button>
      </div>

      {isSnapshotModel && <SnapshotPanel session={session} />}

      {/* Шаг 2 — период (только документы старой модели) */}
      {isSnapshotModel ? (
        <div className="text-sm text-muted-foreground">
          Инвентаризация открыта {formatDateTime(session.started_at)}. Факт —
          упаковки, отсканированные с этого момента (в том числе с телефонов
          через страницу «Медикаменты»); период не задаётся.
        </div>
      ) : (
        <PeriodEditor
          session={session}
          editable
          onSave={(from, to) =>
            void run(async () => {
              await updateMutation.mutateAsync({
                id: session.id,
                payload: { period_from: from, period_to: to },
              });
              // Период изменился — факт нужно пересчитать.
              if (stage === 'fact') setStage('stock');
            })
          }
          saving={updateMutation.isPending}
        />
      )}

      {/* Шаг 3 — фактические остатки */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-muted/30 p-3">
        <div className="text-sm">
          <div className="font-medium">2. Фактические остатки</div>
          <div className="text-muted-foreground">
            {isSnapshotModel
              ? 'Сопоставить сканы этой инвентаризации со снимком учёта'
              : 'Сопоставить отсканированные медикаменты за период'}
          </div>
        </div>
        <Button
          variant={stage === 'stock' ? 'default' : 'outline'}
          disabled={stage === 'empty' || previewMutation.isPending}
          onClick={fillFact}
        >
          {previewMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <ScanLine className="w-4 h-4 mr-1" />
          )}
          Заполнить фактические остатки
        </Button>
      </div>

      {preview && (
        <>
          <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-5">
            <Stat label="Учёт" value={preview.expected_total} />
            <Stat label="Факт" value={showFact ? preview.scanned_total : '—'} />
            <Stat
              label="В наличии"
              value={showFact ? preview.matched_total : '—'}
            />
            <Stat
              label="На вывод"
              value={showFact ? preview.missing_total : '—'}
              tone={showFact && preview.missing_total > 0 ? 'danger' : undefined}
            />
            <Stat
              label="Излишек"
              value={showFact ? preview.surplus_total : '—'}
              tone={showFact && preview.surplus_total > 0 ? 'warn' : undefined}
            />
          </div>

          {showFact && preview.ean_total > 0 && (
            <div className="rounded-md border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
              Сканов без маркировки (EAN, без CIS) за период:{' '}
              <b>{preview.ean_total}</b> — в сверку не входят.
            </div>
          )}

          {showFact ? (
            <CisTabs
              sessionId={session.id}
              kind={cisKind}
              page={cisPage}
              counts={{
                missing: preview.missing_total,
                surplus: preview.surplus_total,
                matched: preview.matched_total,
                late: preview.late_scans_count ?? 0,
              }}
              onKind={(k) => {
                setCisKind(k);
                setCisPage(1);
              }}
              onPage={setCisPage}
            />
          ) : (
            <div className="rounded-md border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
              Учётный остаток загружен: <b>{preview.expected_total}</b> CIS.
              Задайте период и нажмите «Заполнить фактические остатки» для
              сопоставления.
            </div>
          )}
        </>
      )}

      {/* Закрытие */}
      <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-3">
        {isSnapshotModel && closeBlockers.length > 0 && (
          <span className="mr-auto text-sm text-destructive">
            {closeBlockers
              .map((code) => CLOSE_BLOCKER_LABELS[code] ?? code)
              .join(' ')}
          </span>
        )}
        <Button
          disabled={
            closeMutation.isPending ||
            (isSnapshotModel ? closeBlockers.length > 0 : !showFact)
          }
          onClick={() =>
            void run(() => closeMutation.mutateAsync(session.id))
          }
        >
          {closeMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <Lock className="w-4 h-4 mr-1" />
          )}
          Закрыть и зафиксировать
        </Button>
      </div>
    </div>
  );
}

const CIS_PAGE_SIZE = 100;

/** Поштучный список CIS с вкладками: На вывод / Излишек / В наличии. */
function CisTabs({
  sessionId,
  kind,
  page,
  counts,
  onKind,
  onPage,
}: {
  sessionId: string;
  kind: MarkirovkaCisKind;
  page: number;
  counts: Record<MarkirovkaCisKind, number>;
  onKind: (k: MarkirovkaCisKind) => void;
  onPage: (p: number) => void;
}) {
  const { data, isLoading, error, refetch } = useMarkirovkaReconCis(
    sessionId,
    kind,
    page,
    true,
    CIS_PAGE_SIZE,
  );
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / CIS_PAGE_SIZE));

  return (
    <Tabs value={kind} onValueChange={(v) => onKind(v as MarkirovkaCisKind)}>
      <TabsList>
        <TabsTrigger value="missing">
          На вывод ({counts.missing})
        </TabsTrigger>
        <TabsTrigger value="surplus">Излишек ({counts.surplus})</TabsTrigger>
        <TabsTrigger value="matched">
          В наличии ({counts.matched})
        </TabsTrigger>
        {counts.late > 0 && (
          <TabsTrigger value="late">Поздние сканы ({counts.late})</TabsTrigger>
        )}
      </TabsList>

      <TabsContent value={kind}>
        {kind === 'missing' && (
          <p className="mb-2 text-xs text-muted-foreground">
            CIS, которые числятся в учёте, но не отсканированы → на вывод из
            оборота (в 1С).
          </p>
        )}
        {kind === 'late' && (
          <p className="mb-2 text-xs text-muted-foreground">
            Сканы, пришедшие во время закрытия или после него (офлайн-очередь
            телефона). Хранятся отдельно и в итог документа не входят.
          </p>
        )}
        {isLoading ? (
          <LoadingTable rows={6} columns={4} />
        ) : error ? (
          <ErrorState
            title="Не удалось загрузить список"
            description={(error as Error)?.message}
            onAction={() => refetch()}
          />
        ) : (data?.rows.length ?? 0) === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Нет позиций
          </div>
        ) : (
          <>
            <CisTable rows={data!.rows} kind={kind} />
            <div className="mt-3">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={onPage}
              />
            </div>
          </>
        )}
      </TabsContent>
    </Tabs>
  );
}

/** Таблица поштучного списка CIS (одна строка = одна упаковка). */
function CisTable({
  rows,
  kind,
}: {
  rows: MarkirovkaReconCisRow[];
  kind: MarkirovkaCisKind;
}) {
  return (
    <div className="max-h-[44vh] overflow-y-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Препарат</TableHead>
            <TableHead>GTIN</TableHead>
            <TableHead>{kind === 'surplus' ? 'Причина' : 'Накладная'}</TableHead>
            <TableHead>CIS</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.cis}>
              <TableCell className="max-w-[240px] truncate">
                {r.product_name || '—'}
              </TableCell>
              <TableCell className="font-mono text-xs">
                {r.gtin || '—'}
              </TableCell>
              {kind === 'surplus' ? (
                <TableCell>
                  <Badge variant="outline">
                    {REASON_LABELS[r.reason ?? 'unknown'] ?? r.reason}
                  </Badge>
                </TableCell>
              ) : (
                <TableCell className="max-w-[180px] truncate">
                  {r.paper_num || '—'}
                </TableCell>
              )}
              <TableCell className="font-mono text-xs break-all max-w-[200px]">
                {r.cis}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function PeriodEditor({
  session,
  editable,
  onSave,
  saving,
}: {
  session: MarkirovkaReconSession;
  editable: boolean;
  onSave: (from?: string, to?: string) => void;
  saving: boolean;
}) {
  const [from, setFrom] = useState(isoToDate(session.period_from));
  const [to, setTo] = useState(isoToDate(session.period_to));

  useEffect(() => {
    setFrom(isoToDate(session.period_from));
    setTo(isoToDate(session.period_to));
  }, [session.period_from, session.period_to]);

  if (!editable) {
    return (
      <div className="text-sm text-muted-foreground">
        Период: <span className="text-foreground">{formatDate(session.period_from)}</span>{' '}
        — <span className="text-foreground">{formatDate(session.period_to)}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div>
        <label className="text-xs text-muted-foreground">Период с</label>
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="w-40"
        />
      </div>
      <div>
        <label className="text-xs text-muted-foreground">по</label>
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="w-40"
        />
      </div>
      <Button
        variant="outline"
        size="sm"
        disabled={saving}
        onClick={() => onSave(dateToIso(from), dateToIso(to, true))}
      >
        Сохранить период
      </Button>
    </div>
  );
}

function ClosedDocumentBody({
  session,
  run,
}: {
  session: MarkirovkaReconSession;
  run: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const exportMutation = useExportReconSession();
  const exporting = exportMutation.isPending;
  const onExport = () =>
    void run(() => exportMutation.mutateAsync(session.id));

  const [kind, setKind] = useState<'missing' | 'surplus'>('missing');
  const { data, isLoading, error, refetch } = useMarkirovkaReconDiscrepancies(
    session.id,
    { kind, limit: 500 },
  );
  const sent = session.onec_sync_status === 'synced';
  const noDiscrepancies = session.missing_total + session.surplus_total === 0;

  const lateScans = session.late_scans_count ?? 0;

  return (
    <div className="space-y-4">
      {session.fact_source === 'session' ? (
        <div className="text-sm text-muted-foreground">
          Инвентаризация:{' '}
          <span className="text-foreground">
            {formatDateTime(session.started_at)}
          </span>{' '}
          —{' '}
          <span className="text-foreground">
            {formatDateTime(session.closed_at)}
          </span>
          {session.snapshot_at && (
            <> · снимок учёта от {formatDateTime(session.snapshot_at)}</>
          )}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          Период: <span className="text-foreground">{formatDate(session.period_from)}</span>{' '}
          — <span className="text-foreground">{formatDate(session.period_to)}</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-5">
        <Stat label="Учёт" value={session.expected_total} />
        <Stat label="Факт" value={session.scanned_total} />
        <Stat label="В наличии" value={session.matched_total} />
        <Stat
          label="На вывод"
          value={session.missing_total}
          tone={session.missing_total > 0 ? 'danger' : undefined}
        />
        <Stat
          label="Излишек"
          value={session.surplus_total}
          tone={session.surplus_total > 0 ? 'warn' : undefined}
        />
      </div>

      {session.ean_total > 0 && (
        <div className="rounded-md border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
          Сканов без маркировки (EAN, без CIS) за период:{' '}
          <b>{session.ean_total}</b> — вне сверки недостач/излишков.
        </div>
      )}

      {lateScans > 0 && (
        <div className="rounded-md border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
          Поздние сканы: <b>{lateScans}</b> — пришли с телефонов после закрытия
          (офлайн-очередь). Итог документа и документ для 1С они не меняют.
        </div>
      )}

      <div className="flex items-center justify-between rounded-md border bg-muted/30 px-4 py-2">
        <div className="text-sm">
          {sent ? (
            <span className="flex items-center gap-1 text-foreground">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              Отправлено в 1С · {formatDate(session.onec_synced_at)}
            </span>
          ) : noDiscrepancies ? (
            <span className="text-muted-foreground">
              Расхождений нет — отправка не требуется
            </span>
          ) : (
            <span className="text-muted-foreground">
              Готово к отправке в 1С
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={exporting || sent || noDiscrepancies}
          onClick={onExport}
        >
          {exporting ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-1" />
          )}
          Отправить в 1С
        </Button>
      </div>

      <Tabs
        value={kind}
        onValueChange={(v) => setKind(v as 'missing' | 'surplus')}
      >
        <TabsList>
          <TabsTrigger value="missing">
            На вывод из оборота ({session.missing_total})
          </TabsTrigger>
          <TabsTrigger value="surplus">
            Излишки ({session.surplus_total})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={kind}>
          {isLoading ? (
            <LoadingTable rows={5} columns={4} />
          ) : error ? (
            <ErrorState
              title="Не удалось загрузить расхождения"
              description={(error as Error)?.message}
              onAction={() => refetch()}
            />
          ) : (data?.rows?.length ?? 0) === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {kind === 'missing' ? 'Недостач нет' : 'Излишков нет'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Наименование</TableHead>
                  <TableHead>GTIN</TableHead>
                  <TableHead>
                    {kind === 'missing' ? 'Накладная' : 'Причина'}
                  </TableHead>
                  <TableHead>CIS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.rows.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="max-w-[240px] truncate">
                      {d.product_name || '—'}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {d.gtin || '—'}
                    </TableCell>
                    {kind === 'missing' ? (
                      <TableCell className="max-w-[180px] truncate">
                        {d.paper_num || '—'}
                      </TableCell>
                    ) : (
                      <TableCell>
                        <Badge variant="outline">
                          {REASON_LABELS[d.reason ?? 'unknown'] ?? d.reason}
                        </Badge>
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-xs break-all max-w-[200px]">
                      {d.cis}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** Состояние снимка учёта документа и предупреждения по нему. */
function SnapshotPanel({ session }: { session: MarkirovkaReconSession }) {
  const status = session.snapshot_status ?? 'none';
  const unlinked = session.snapshot_unlinked_total ?? 0;
  const late = session.late_scans_count ?? 0;
  const beforeSnapshot = session.scans_before_snapshot ?? 0;

  return (
    <div className="space-y-3 rounded-lg border p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Снимок учёта:</span>
        <Badge
          variant={
            status === 'ready'
              ? 'success'
              : status === 'failed'
                ? 'destructive'
                : 'secondary'
          }
        >
          {SNAPSHOT_STATUS_LABELS[status] ?? status}
        </Badge>
        {session.snapshot_at && (
          <span className="text-muted-foreground">
            от {formatDateTime(session.snapshot_at)}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-5">
        <Stat label="В снимке" value={session.snapshot_total ?? 0} />
        <Stat
          label="Вне учёта (госпоставки)"
          value={session.snapshot_excluded_total ?? 0}
        />
        <Stat
          label="Без накладной"
          value={unlinked}
          tone={unlinked > 0 ? 'danger' : undefined}
        />
        <Stat label="Отсканировано" value={session.scanned_in_period ?? 0} />
        <Stat
          label="Поздние сканы"
          value={late}
          tone={late > 0 ? 'warn' : undefined}
        />
      </div>

      {status === 'failed' && session.snapshot_error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
          Снимок не создан: {session.snapshot_error}
        </div>
      )}

      {unlinked > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <b>Критично:</b> в снимке {unlinked} марок без накладной-источника.
            Для них нельзя проверить признак «вне учёта» — бесплатная
            госпоставка может попасть «На вывод» и уйти на списание. Закрытие
            документа заблокировано.
          </span>
        </div>
      )}

      {beforeSnapshot > 0 && (
        <div className="rounded-md border border-amber-300/50 bg-amber-50 px-3 py-2 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
          Снимок зафиксирован после начала сканирования: {beforeSnapshot}{' '}
          сканов пришли раньше него. Проверьте излишки: упаковка, выбывшая в
          edo до снимка, окажется среди них.
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: 'danger' | 'warn';
}) {
  const toneClass =
    tone === 'danger'
      ? 'text-destructive'
      : tone === 'warn'
        ? 'text-amber-600'
        : '';
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${toneClass}`}>
        {value}
      </div>
    </div>
  );
}
